import React from 'react';
import PropTypes from 'prop-types';

/**
 * App-level error boundary (#1020 finding 21).
 *
 * Before this, a render error anywhere in the tree unmounted the whole app and
 * left a blank white page. React has no default fallback: an uncaught error in
 * render tears the root down and shows nothing at all, so the user sees a page
 * that looks broken with no way forward and no indication anything was logged.
 *
 * The only boundary in the codebase was `MdxErrorBoundary`, scoped to the rich
 * text field. Everything outside that one component was unprotected.
 *
 * DELIBERATELY DEPENDENCY-FREE IN THE FALLBACK. No MUI, no theme, no context,
 * no router — inline styles and plain elements only. A boundary that renders
 * MUI can throw while rendering its own fallback (the ThemeProvider may be the
 * very thing that failed), and a boundary that fails is worse than none: React
 * then propagates to the next boundary up, and there is none above this one.
 *
 * Wrapped around <AppBoot/> in index.js, which is outside every provider.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The backend has Sentry (#1041) and the frontend tier still does not —
    // finding 3 is half done. Until it is wired, the console is the only sink,
    // so log the component stack rather than the bare error: without it a
    // minified production trace does not say which component failed.
    console.error('[RMDV] unhandled render error', error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        data-testid="app-error-boundary"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          color: '#1a1a1a',
          background: '#fafafa',
        }}
      >
        <div style={{ maxWidth: '560px' }}>
          <h1 style={{ fontSize: '1.25rem', margin: '0 0 12px' }}>
            Something went wrong on this page
          </h1>
          <p style={{ margin: '0 0 16px', lineHeight: 1.5 }}>
            The page failed to load and could not recover on its own. Nothing you
            had saved is affected — reloading is safe, and it usually works.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="app-error-reload"
            style={{
              padding: '8px 16px',
              fontSize: '0.95rem',
              cursor: 'pointer',
              border: '1px solid #1a1a1a',
              borderRadius: '6px',
              background: '#1a1a1a',
              color: '#fff',
            }}
          >
            Reload the page
          </button>
          <p style={{ margin: '16px 0 0', fontSize: '0.85rem', color: '#666' }}>
            If it keeps happening, the details are in your browser console.
          </p>
        </div>
      </div>
    );
  }
}

AppErrorBoundary.propTypes = {
  children: PropTypes.node,
};

export default AppErrorBoundary;
